import React, { useState, forwardRef } from "react";
import { formatRaceDate } from "@tvg/formatter/dates";
import {
  Icon,
  Paragraph,
  Tag,
  Tooltip,
  useColorTokens
} from "@tvg/design-system";
import { getLength, getOdds, getPosition } from "./functions";
import { PastRaceUIProps } from "./types";
import ExtraInfoComp from "./ExtraInfo";
import {
  Container,
  ContainerPosition,
  ExtraInfo,
  Stats,
  TagWrapper
} from "./styled-components";

const PastRaceDesktop = forwardRef<HTMLDivElement, PastRaceUIProps>(
  (
    {
      isVideoAvailable,
      race,
      tooltipContent,
      hasTooltip,
      surfaceShortname,
      surfaceShortnameFontFamily,
      surfaceCondition,
      distanceFontFamily,
      distance,
      raceClassShortname,
      runner,
      purse,
      beatenDistance,
      beatenDistanceStatus,
      surfaceConditionFontFamily
    },
    ref
  ) => {
    const colorTokens = useColorTokens();
    const [defaultColor, setDefaultColor] = useState(
      colorTokens.content.subtle
    );

    const handleMouseOver = () => {
      setDefaultColor(colorTokens.content.brandPrimary);
    };

    const handleMouseLeave = () => {
      setDefaultColor(colorTokens.content.subtle);
    };

    return (
      <Container
        onMouseOver={handleMouseOver}
        onMouseLeave={handleMouseLeave}
        data-qa-label="runner-handicap-inline-pp-container"
      >
        <Stats>
          <Icon
            size="s"
            name={isVideoAvailable ? "replay" : "noVideo"}
            qaLabel="runner-handicap-inline-pp-video"
            lineColor={race.video ? defaultColor : colorTokens.content.subtle3}
          />
          <Paragraph
            qaLabel="runner-handicap-inline-pp-date"
            color={defaultColor}
          >
            {formatRaceDate(race.date)}
          </Paragraph>
          <Paragraph
            qaLabel="runner-handicap-inline-pp-track"
            color={defaultColor}
          >
            {race.track.code}
          </Paragraph>
          {/* eslint-disable-next-line react/no-danger */}
          <Tooltip content={tooltipContent} isDisabled={!hasTooltip}>
            <ExtraInfo
              ref={ref}
              role="paragraph"
              data-qa-label="runner-handicap-inline-pp-match-conditions"
              showDefault={defaultColor === colorTokens.content.subtle}
            >
              <ExtraInfoComp
                defaultColor={defaultColor}
                distance={distance}
                surfaceShortname={surfaceShortname}
                surfaceShortnameFontFamily={surfaceShortnameFontFamily}
                distanceFontFamily={distanceFontFamily}
                surfaceConditionFontFamily={surfaceConditionFontFamily}
                surfaceCondition={surfaceCondition}
                purse={purse}
                raceClassShortname={raceClassShortname}
              />
            </ExtraInfo>
          </Tooltip>
        </Stats>
        <ContainerPosition>
          <Paragraph
            qaLabel="runner-handicap-inline-pp-position"
            color={
              runner && runner.finishPosition === 1
                ? colorTokens.background.positive
                : defaultColor
            }
            fontFamily="bold"
          >
            {getPosition(runner, race.numRunners)}
          </Paragraph>
          <Paragraph
            qaLabel="runner-handicap-inline-pp-length"
            color={colorTokens.content.subtle2}
            fontWeight={400}
            fontFamily="regular"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {getLength(beatenDistance, beatenDistanceStatus)}
          </Paragraph>
        </ContainerPosition>
        {runner && runner.favorite ? (
          <TagWrapper>
            <Tag
              qaLabel="runner-handicap-inline-pp-odds-fav"
              label={getOdds(runner)}
              size="s"
              variant="promo"
            />
          </TagWrapper>
        ) : (
          <Paragraph
            qaLabel="runner-handicap-inline-pp-odds"
            color={defaultColor}
            textAlign="right"
          >
            {getOdds(runner)}
          </Paragraph>
        )}
      </Container>
    );
  }
);

export default PastRaceDesktop;
